import { BehaviorSubject, PartialObserver } from 'rxjs';
import { Injectable } from '@angular/core';

import { Widget, WidgetType } from '../models/widget';
import { User } from '../models/user';

import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class DashboardWidgetService {
	private readonly informationWidgetChangesSubject = new BehaviorSubject(<Widget[]> []);
	private readonly indicatorWidgetChangesSubject = new BehaviorSubject(<Widget[]> []);

	private keyInformationWidgets: Widget[] = [];
	private keyIndicatorWidgets: Widget[] = [];

	private copyKeyInformationWidgets: Widget[] = [];
	private copyKeyIndicatorWidgets: Widget[] = [];

	private readonly user?: User;
	private readonly widgetSizeMap = new Map([
		['RtsWidget', 'block-size-1'],
		['IssueFollowUpWidget', 'block-size-1'],
		['BiggestStocksWidget', 'block-size-1'],
	]);

	constructor(
		private readonly userService: UserService
	) {
		this.user = this.userService.user;

		for (const widget of this.user.widgets) {
			if (widget.type === WidgetType.INDICATOR) {
				this.keyIndicatorWidgets.push(widget);
				this.copyKeyIndicatorWidgets = this.keyIndicatorWidgets.slice();
			} else {
				this.keyInformationWidgets.push(widget);
				this.copyKeyInformationWidgets = this.keyInformationWidgets.slice();
			}
		}

		this.informationWidgetChangesSubject.next(this.keyInformationWidgets.slice());
		this.indicatorWidgetChangesSubject.next(this.keyIndicatorWidgets.slice());
	}

	subscribeToInformationWidget(observer: PartialObserver<Widget[]>) {
		return this.informationWidgetChangesSubject.subscribe(observer);
	}

	subscribeToIndicatorWidget(observer: PartialObserver<Widget[]>) {
		return this.indicatorWidgetChangesSubject.subscribe(observer);
	}

	async validateWidgets() {
		this.copyKeyIndicatorWidgets = this.keyIndicatorWidgets.slice();
		this.copyKeyInformationWidgets = this.keyInformationWidgets.slice();

		if (this.user) {
			this.user.widgets = this.keyInformationWidgets.concat(this.keyIndicatorWidgets);
			await this.userService.updateUserDashboardWidgets(this.user);
		}

		return this.userService.updateUserData();
	}

	changeOrder(widgets: Widget[]) {
		if (widgets[0].type === WidgetType.INDICATOR) {
			this.keyIndicatorWidgets = widgets.slice();
			this.updatePriority(this.keyIndicatorWidgets);
		} else {
			this.keyInformationWidgets = widgets.slice();
			this.updatePriority(this.keyInformationWidgets);
		}
	}

	cancelWidgets() {
		this.updatePriority(this.copyKeyIndicatorWidgets);
		this.updatePriority(this.copyKeyInformationWidgets);
		this.keyIndicatorWidgets = this.copyKeyIndicatorWidgets.slice();
		this.keyInformationWidgets = this.copyKeyInformationWidgets.slice();
		this.informationWidgetChangesSubject.next(this.keyInformationWidgets.slice());
		this.indicatorWidgetChangesSubject.next(this.keyIndicatorWidgets.slice());
	}

	addWidget(widget: Widget) {
		if (widget.type === WidgetType.INDICATOR) {
			this.keyIndicatorWidgets.push(widget);
			this.updatePriority(this.keyIndicatorWidgets);
			this.indicatorWidgetChangesSubject.next(this.keyIndicatorWidgets.slice());
		} else {
			this.keyInformationWidgets.push(widget);
			this.updatePriority(this.keyInformationWidgets);
			this.informationWidgetChangesSubject.next(this.keyInformationWidgets.slice());
		}
	}

	replaceWidget(oldWidget: Widget, newWidget: Widget) {
		if (oldWidget.type === WidgetType.INDICATOR) {
			const index = this.keyIndicatorWidgets.indexOf(oldWidget);
			this.keyIndicatorWidgets[index] = newWidget;
			this.updatePriority(this.keyIndicatorWidgets);
			this.indicatorWidgetChangesSubject.next(this.keyIndicatorWidgets.slice());
		} else {
			const index = this.keyInformationWidgets.indexOf(oldWidget);
			this.keyInformationWidgets[index] = newWidget;
			this.updatePriority(this.keyInformationWidgets);
			this.informationWidgetChangesSubject.next(this.keyInformationWidgets.slice());
		}
	}

	removeWidget(widget: Widget) {
		if (widget.type === WidgetType.INDICATOR) {
			this.keyIndicatorWidgets.splice(this.keyIndicatorWidgets.indexOf(widget), 1);
			this.updatePriority(this.keyIndicatorWidgets);
			this.indicatorWidgetChangesSubject.next(this.keyIndicatorWidgets.slice());
		} else {
			this.keyInformationWidgets.splice(this.keyInformationWidgets.indexOf(widget), 1);
			this.updatePriority(this.keyInformationWidgets);
			this.informationWidgetChangesSubject.next(this.keyInformationWidgets.slice());
		}
	}

	sizeWidget(widgetName: string) {
		const widgetSize = this.widgetSizeMap.get(widgetName);
		if (!widgetSize) {
			return 'block-size-1';
		}
		return widgetSize;
	}

	private updatePriority(widgets: Widget[]) {
		let i = 0;
		for (const widget of widgets) {
			widget.priority = i;
			i++;
		}
	}
}
